import {
    Box,
    List,
    ListItemButton,
    ListItemText,
    Paper,
    Popper,
} from "@mui/material";
import {
    EditorContent,
    EditorRoot,
    useEditor as useCurrentEditor,
} from "novel";
import { StarterKit, Placeholder } from "novel";
import { Markdown } from "tiptap-markdown";
import { Extension, type JSONContent } from "@tiptap/core";
import { Plugin, PluginKey, type Transaction } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";
import Suggestion from "@tiptap/suggestion";
import {
    forwardRef,
    type RefObject,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
} from "react";
import type { Editor } from "@tiptap/core";
import type { Node as ProseMirrorNode } from "@tiptap/pm/model";

import {
    findReferenceMentions,
    resolveReferenceMention,
    type ReferenceResolutionPolicy,
} from "@/utils/reference-mentions";

export interface EditorHeading {
    level: number;
    index: number;
    label: string;
    isRoot: boolean;
}

function walkEditorHeadings(
    doc: ProseMirrorNode,
    visit: (heading: EditorHeading, pos: number) => void
) {
    const indexesByLevel = new Map<number, number>();

    doc.descendants((node, pos, parent) => {
        if (node.type.name !== "heading") return;

        const level = node.attrs.level as number;
        const index = indexesByLevel.get(level) ?? 0;
        indexesByLevel.set(level, index + 1);
        visit(
            {
                level,
                index,
                label: node.textContent.trim(),
                isRoot: parent === doc,
            },
            pos
        );
        return false;
    });
}

function readEditorHeadings(doc: ProseMirrorNode): readonly EditorHeading[] {
    const headings: EditorHeading[] = [];
    walkEditorHeadings(doc, (heading) => headings.push(heading));
    return headings;
}

// ── Decoration: @-reference pills ────────────────────────────────────

// The editor scans ProseMirror text nodes, where a backslash the author typed
// is a literal character. The engine and the backend scan serialized markdown,
// where a backslash is an escape. Decoding here would pill a typed @my\_ref as
// valid while publish reads it as a different, broken target.
const DOCUMENT_TEXT_SCAN = { decodeMarkdownEscapes: false } as const;

const fileRefPluginKey = new PluginKey("fileRefPills");
const fileRefSuggestionPluginKey = new PluginKey<{
    active: boolean;
    range: { from: number; to: number };
}>("fileRefSuggestion");

function buildDecorations(
    doc: ProseMirrorNode,
    validNames: readonly string[],
    policy: ReferenceResolutionPolicy,
    vocabulary: readonly string[]
): DecorationSet {
    if (policy === "case-insensitive" && validNames.length === 0) {
        return DecorationSet.empty;
    }

    const targets = validNames.map((name) => ({ identity: name, name }));
    const decorations: Decoration[] = [];
    doc.descendants((node: ProseMirrorNode, pos: number) => {
        if (!node.isText || !node.text) return;
        for (const mention of findReferenceMentions(
            node.text,
            vocabulary,
            DOCUMENT_TEXT_SCAN
        )) {
            const { resolvedIdentity } = resolveReferenceMention(
                mention,
                targets,
                policy
            );
            if (resolvedIdentity === null && policy !== "exact-case") {
                continue;
            }
            decorations.push(
                Decoration.inline(pos + mention.start, pos + mention.end, {
                    class:
                        resolvedIdentity === null
                            ? "file-ref-pill-invalid"
                            : "file-ref-pill",
                })
            );
        }
    });
    return DecorationSet.create(doc, decorations);
}

// ── Suggestion popup state ───────────────────────────────────────────

interface SuggestionPopupState {
    active: boolean;
    items: string[];
    command: ((item: string) => void) | null;
    clientRect: (() => DOMRect | null) | null;
}

function EditorBinding({
    editorRef,
    onHeadingsChangeRef,
}: {
    editorRef: RefObject<Editor | null>;
    onHeadingsChangeRef: {
        current: ((headings: readonly EditorHeading[]) => void) | undefined;
    };
}) {
    const { editor } = useCurrentEditor();

    useEffect(() => {
        if (!editor) return;

        editorRef.current = editor;
        onHeadingsChangeRef.current?.(readEditorHeadings(editor.state.doc));

        return () => {
            if (editorRef.current === editor) editorRef.current = null;
        };
    }, [editor, editorRef, onHeadingsChangeRef]);

    return null;
}

const INITIAL_POPUP: SuggestionPopupState = {
    active: false,
    items: [],
    command: null,
    clientRect: null,
};

// ── Public API ───────────────────────────────────────────────────────

export interface MarkdownEditorHandle {
    /** Insert text at the editor's stored selection. */
    insertText: (text: string) => void;
    /**
     * Select the first UNRESOLVED mention of `typedTarget` (given without the
     * leading @) and scroll it into view. Resolved mentions are skipped, so a
     * broken target that prefixes a valid name reaches the broken one, and a
     * target whose every mention resolves selects nothing.
     */
    scrollToMention: (typedTarget: string) => void;
    /**
     * Index counts heading nodes of `level` in document order; derive it from
     * onHeadingsChange, never from a source scan.
     */
    scrollToHeading: (target: { level: number; index: number }) => boolean;
}

interface MarkdownEditorProps {
    /** Markdown string — set once on mount. Use key prop to reset. */
    value: string;
    onChange: (markdown: string) => void;
    placeholder?: string;
    minHeight?: number;
    error?: boolean;
    errorId?: string;
    labelId?: string;
    label?: string;
    /** Names a mention resolves against, and the autocomplete list. */
    suggestions?: readonly string[];
    /** `exact-case` also marks unresolved mentions across their typed target. */
    referenceResolution?: ReferenceResolutionPolicy;
    /**
     * Names a mention may be recognized from. Recognition only: resolution and
     * autocomplete stay on `suggestions`.
     */
    recognitionVocabulary?: readonly string[];
    onHeadingsChange?: (headings: readonly EditorHeading[]) => void;
}

export const MarkdownEditor = forwardRef<
    MarkdownEditorHandle,
    MarkdownEditorProps
>(
    (
        {
            value,
            onChange,
            placeholder = "Start writing...",
            minHeight = 200,
            error = false,
            errorId,
            labelId,
            label,
            suggestions = [],
            referenceResolution = "case-insensitive",
            recognitionVocabulary,
            onHeadingsChange,
        },
        ref
    ) => {
        const vocabulary = recognitionVocabulary ?? suggestions;
        const editorProps = useMemo(() => {
            const attributes: Record<string, string> = {
                role: "textbox",
                "aria-multiline": "true",
            };
            if (labelId) attributes["aria-labelledby"] = labelId;
            else if (label) attributes["aria-label"] = label;
            if (error) {
                attributes["aria-invalid"] = "true";
                if (errorId) attributes["aria-describedby"] = errorId;
            }
            return { attributes };
        }, [error, errorId, labelId, label]);
        const editorRef = useRef<Editor | null>(null);
        const [initialValue] = useState(value);
        // Novel forwards strings here but narrows the prop to JSONContent.
        const initialContent = initialValue as unknown as JSONContent;
        const onHeadingsChangeRef = useRef(onHeadingsChange);
        onHeadingsChangeRef.current = onHeadingsChange;
        const suggestionsRef = useRef<readonly string[]>(suggestions);
        const vocabularyRef = useRef<readonly string[]>(vocabulary);
        const resolutionRef =
            useRef<ReferenceResolutionPolicy>(referenceResolution);
        const [popup, setPopup] = useState<SuggestionPopupState>(INITIAL_POPUP);
        const [selectedIndex, setSelectedIndex] = useState(0);
        const popupRef = useRef(INITIAL_POPUP);
        const selectedIndexRef = useRef(0);

        useEffect(() => {
            suggestionsRef.current = suggestions;
            vocabularyRef.current = vocabulary;
            resolutionRef.current = referenceResolution;
            const editor = editorRef.current;
            if (editor) {
                const { tr } = editor.state;
                editor.view.dispatch(tr.setMeta(fileRefPluginKey, true));
            }
        }, [suggestions, vocabulary, referenceResolution]);

        useImperativeHandle(ref, () => ({
            insertText(text: string) {
                const editor = editorRef.current;
                if (!editor || text === "") return;
                // insertContent parses a string as HTML, which turns a name
                // holding & or < into markup and a typed U+00A0 into a literal
                // "&nbsp;". A text node inserts the characters as given.
                editor
                    .chain()
                    .focus()
                    .insertContent({ type: "text", text })
                    .run();
            },
            scrollToMention(typedTarget: string) {
                const editor = editorRef.current;
                if (!editor) return;
                const targets = suggestionsRef.current.map((name) => ({
                    identity: name,
                    name,
                }));
                let found = false;
                editor.state.doc.descendants((node, pos) => {
                    if (found) return false;
                    if (!node.isText || !node.text) return;
                    for (const mention of findReferenceMentions(
                        node.text,
                        vocabularyRef.current,
                        DOCUMENT_TEXT_SCAN
                    )) {
                        if (mention.typedTarget !== typedTarget) continue;
                        const { resolvedIdentity } = resolveReferenceMention(
                            mention,
                            targets,
                            resolutionRef.current
                        );
                        if (resolvedIdentity !== null) continue;
                        found = true;
                        editor
                            .chain()
                            .focus()
                            .setTextSelection({
                                from: pos + mention.start,
                                to: pos + mention.end,
                            })
                            .run();
                        editor.view.dispatch(editor.state.tr.scrollIntoView());
                        return false;
                    }
                });
            },
            scrollToHeading(target: { level: number; index: number }) {
                const editor = editorRef.current;
                if (!editor || target.index < 0) return false;
                let found = false;
                walkEditorHeadings(editor.state.doc, (heading, pos) => {
                    if (
                        found ||
                        heading.level !== target.level ||
                        heading.index !== target.index
                    ) {
                        return;
                    }
                    found = true;
                    editor
                        .chain()
                        .focus()
                        .setTextSelection(pos + 1)
                        .run();
                    editor.view.dispatch(editor.state.tr.scrollIntoView());
                });
                return found;
            },
        }));

        // Build extensions once, closing over refs for live data access
        const extensionsRef = useRef<
            ReturnType<typeof Extension.create>[] | null
        >(null);
        if (!extensionsRef.current) {
            extensionsRef.current = [
                StarterKit.configure({
                    horizontalRule: false,
                    codeBlock: {
                        HTMLAttributes: { spellcheck: "false" },
                    },
                }),
                Placeholder.configure({
                    placeholder,
                    emptyEditorClass: "is-editor-empty",
                }),
                Markdown.configure({
                    html: false,
                    transformPastedText: true,
                    transformCopiedText: true,
                }),
                // Chrome rebalances a space typed at the end of a pill span
                // into U+00A0. The recognizer and the backend publish resolver
                // both match names with U+0020 only, so a mention holding one
                // is unresolvable rather than merely odd-looking.
                Extension.create({
                    name: "normalizeNonBreakingSpace",
                    onBeforeCreate() {
                        const { content } = this.editor.options;
                        if (typeof content === "string") {
                            this.editor.options.content = content.replace(
                                /\u00A0/g,
                                " "
                            );
                        }
                    },
                    addProseMirrorPlugins() {
                        return [
                            new Plugin({
                                appendTransaction(
                                    transactions,
                                    _oldState,
                                    newState
                                ) {
                                    if (
                                        !transactions.some(
                                            ({ docChanged }) => docChanged
                                        )
                                    ) {
                                        return null;
                                    }
                                    let tr: Transaction | null = null;
                                    newState.doc.descendants((node, pos) => {
                                        if (!node.isText || !node.text) return;
                                        const { text } = node;
                                        for (
                                            let at = text.indexOf("\u00A0");
                                            at !== -1;
                                            at = text.indexOf("\u00A0", at + 1)
                                        ) {
                                            tr ??= newState.tr;
                                            tr.insertText(
                                                " ",
                                                pos + at,
                                                pos + at + 1
                                            );
                                        }
                                    });
                                    return tr;
                                },
                            }),
                        ];
                    },
                }),
                // Decoration plugin: resolved mentions pill, and under
                // exact-case unresolved ones pill as invalid
                Extension.create({
                    name: "fileRefPills",
                    addProseMirrorPlugins() {
                        return [
                            new Plugin({
                                key: fileRefPluginKey,
                                state: {
                                    init(_, { doc }) {
                                        return buildDecorations(
                                            doc,
                                            suggestionsRef.current,
                                            resolutionRef.current,
                                            vocabularyRef.current
                                        );
                                    },
                                    apply(tr, old, _oldState, newState) {
                                        if (
                                            tr.docChanged ||
                                            tr.getMeta(fileRefPluginKey)
                                        ) {
                                            return buildDecorations(
                                                newState.doc,
                                                suggestionsRef.current,
                                                resolutionRef.current,
                                                vocabularyRef.current
                                            );
                                        }
                                        return old;
                                    },
                                },
                                props: {
                                    decorations(state) {
                                        return fileRefPluginKey.getState(state);
                                    },
                                },
                            }),
                        ];
                    },
                }),
                // Suggestion plugin: autocomplete dropdown on @
                Extension.create({
                    name: "fileRefSuggestion",
                    addProseMirrorPlugins() {
                        return [
                            Suggestion<string, string>({
                                pluginKey: fileRefSuggestionPluginKey,
                                editor: this.editor,
                                char: "@",
                                allowSpaces: true,
                                decorationClass: "",
                                items: ({ query }) => {
                                    const names = suggestionsRef.current;
                                    if (names.length === 0) return [];
                                    const q = query.toLowerCase();
                                    return names.filter((n) =>
                                        n.toLowerCase().includes(q)
                                    );
                                },
                                command: ({
                                    editor: ed,
                                    range,
                                    props: name,
                                }) => {
                                    ed.chain()
                                        .focus()
                                        .deleteRange(range)
                                        .insertContent({
                                            type: "text",
                                            text: `@${name} `,
                                        })
                                        .run();
                                },
                                render: () => ({
                                    onStart(props) {
                                        selectedIndexRef.current = 0;
                                        setSelectedIndex(0);
                                        const s: SuggestionPopupState = {
                                            active: props.items.length > 0,
                                            items: props.items,
                                            command: (item) =>
                                                props.command(item),
                                            clientRect:
                                                props.clientRect ?? null,
                                        };
                                        popupRef.current = s;
                                        setPopup(s);
                                    },
                                    onUpdate(props) {
                                        selectedIndexRef.current = 0;
                                        setSelectedIndex(0);
                                        const s: SuggestionPopupState = {
                                            active: props.items.length > 0,
                                            items: props.items,
                                            command: (item) =>
                                                props.command(item),
                                            clientRect:
                                                props.clientRect ?? null,
                                        };
                                        popupRef.current = s;
                                        setPopup(s);
                                    },
                                    onKeyDown({ event }) {
                                        const { items } = popupRef.current;
                                        if (!items.length) return false;
                                        if (event.key === "ArrowDown") {
                                            const next =
                                                (selectedIndexRef.current + 1) %
                                                items.length;
                                            selectedIndexRef.current = next;
                                            setSelectedIndex(next);
                                            return true;
                                        }
                                        if (event.key === "ArrowUp") {
                                            const next =
                                                (selectedIndexRef.current -
                                                    1 +
                                                    items.length) %
                                                items.length;
                                            selectedIndexRef.current = next;
                                            setSelectedIndex(next);
                                            return true;
                                        }
                                        if (
                                            event.key === "Enter" ||
                                            event.key === "Tab"
                                        ) {
                                            const item =
                                                items[selectedIndexRef.current];
                                            if (
                                                item &&
                                                popupRef.current.command
                                            ) {
                                                popupRef.current.command(item);
                                            }
                                            return true;
                                        }
                                        if (event.key === "Escape") {
                                            setPopup(INITIAL_POPUP);
                                            popupRef.current = INITIAL_POPUP;
                                            return true;
                                        }
                                        return false;
                                    },
                                    onExit() {
                                        setPopup(INITIAL_POPUP);
                                        popupRef.current = INITIAL_POPUP;
                                    },
                                }),
                            }),
                        ];
                    },
                }),
            ];
        }

        // Virtual anchor for Popper positioning
        const anchorEl =
            popup.active && popup.clientRect
                ? {
                      getBoundingClientRect: () =>
                          popup.clientRect?.() ?? new DOMRect(),
                  }
                : null;

        return (
            <Box sx={{ position: "relative" }}>
                <Box
                    sx={{
                        border: 1,
                        borderColor: error ? "error.main" : "divider",
                        borderRadius: "8px",
                        overflow: "hidden",
                        transition: "border-color 0.2s",
                        "&:focus-within": {
                            borderColor: error ? "error.main" : "primary.main",
                        },
                        "& .ProseMirror": {
                            minHeight,
                            px: 2,
                            py: 1.5,
                            outline: "none",
                            fontFamily: "inherit",
                            fontSize: "1rem",
                            lineHeight: 1.7,
                            "& p": { m: 0, mb: 0.5 },
                            "& h1, & h2, & h3, & h4": {
                                mt: 2,
                                mb: 1,
                                fontWeight: 600,
                                "&:first-child": { mt: 0 },
                            },
                            "& h1": { fontSize: "1.5rem" },
                            "& h2": { fontSize: "1.25rem" },
                            "& h3": { fontSize: "1.125rem" },
                            "& h4": { fontSize: "1rem" },
                            "& ul, & ol": { pl: 3, my: 0.5 },
                            "& li": { mb: 0.25 },
                            "& blockquote": {
                                borderLeft: 3,
                                borderColor: "divider",
                                pl: 2,
                                ml: 0,
                                color: "text.secondary",
                                fontStyle: "italic",
                            },
                            "& code": {
                                bgcolor: "grey.100",
                                borderRadius: "4px",
                                px: 0.5,
                                py: 0.25,
                                fontSize: "0.8125rem",
                                fontFamily: "monospace",
                            },
                            "& pre": {
                                bgcolor: "grey.100",
                                borderRadius: "6px",
                                p: 1.5,
                                overflow: "auto",
                                "& code": {
                                    bgcolor: "transparent",
                                    p: 0,
                                },
                            },
                            "& p.is-editor-empty:first-child::before": {
                                content: "attr(data-placeholder)",
                                color: "text.disabled",
                                float: "left",
                                height: 0,
                                pointerEvents: "none",
                            },
                            "& .file-ref-pill, & .file-ref-pill-invalid": {
                                bgcolor: "grey.200",
                                color: "text.primary",
                                fontWeight: 500,
                                fontSize: "inherit",
                                px: 0.75,
                                py: 0.25,
                                borderRadius: "999px",
                                WebkitBoxDecorationBreak: "clone",
                                boxDecorationBreak: "clone",
                            },
                            "& .file-ref-pill-invalid": {
                                bgcolor: "transparent",
                                color: "error.main",
                                border: 1,
                                borderColor: "error.main",
                                borderStyle: "dashed",
                            },
                        },
                    }}
                >
                    <EditorRoot>
                        <EditorContent
                            extensions={extensionsRef.current!}
                            editorProps={editorProps}
                            initialContent={initialContent}
                            onCreate={({ editor }) => {
                                editor.commands.setTextSelection(
                                    editor.state.doc.content.size
                                );
                            }}
                            onUpdate={({ editor }) => {
                                const md =
                                    editor.storage.markdown?.getMarkdown?.() ??
                                    "";
                                onChange(md);
                                onHeadingsChangeRef.current?.(
                                    readEditorHeadings(editor.state.doc)
                                );
                            }}
                        >
                            <EditorBinding
                                editorRef={editorRef}
                                onHeadingsChangeRef={onHeadingsChangeRef}
                            />
                        </EditorContent>
                    </EditorRoot>
                </Box>

                {/* Autocomplete dropdown */}
                {anchorEl && (
                    <Popper
                        open
                        anchorEl={anchorEl}
                        placement="bottom-start"
                        sx={{ zIndex: 1300 }}
                    >
                        <Paper
                            elevation={3}
                            sx={{
                                mt: 0.5,
                                minWidth: 180,
                                maxHeight: 200,
                                overflow: "auto",
                            }}
                        >
                            <List dense disablePadding>
                                {popup.items.map((name, i) => (
                                    <ListItemButton
                                        key={name}
                                        selected={i === selectedIndex}
                                        onClick={() => popup.command?.(name)}
                                        sx={{ py: 0.5, px: 1.5 }}
                                    >
                                        <ListItemText
                                            primary={`@${name}`}
                                            primaryTypographyProps={{
                                                fontSize: "0.8125rem",
                                            }}
                                        />
                                    </ListItemButton>
                                ))}
                            </List>
                        </Paper>
                    </Popper>
                )}
            </Box>
        );
    }
);
MarkdownEditor.displayName = "MarkdownEditor";
